import AgTechDataActionSurface from "@agtech/core/data/actions/AgTechDataActionSurfaces"
import { useDataActionExecutor } from "@agtech/core/data/actions/AgTechDataActions"
import { AgTechFormContext } from "@agtech/core/forms/AgTechFormContext"
import { AgTechFormActionProps, AgTechFormSubmissionButton } from "@agtech/core/forms/AgTechFormProps"
import React, { useState, useRef, useEffect } from "react"
import { AgTechButton } from "../../Buttons/AgTechButtons"
import { AgTechForm } from "../../Forms/AgTechForm"
import AgTechHeaderDetail from "../../Headers/AgTechHeaderDetail"
import AgTechCloseIcon from "../../Icons/AgTechCloseIcon"
import AgTechFadeInView from "../../Pages/Loading/AgTechFadeInView"
import { AgTechFormDialogBehaviorProps, AgTechFormDialogData, AgTechFormDialogDataProps, AgTechFormDialogType, useAgTechFormDialog } from "./AgTechFormDialog"
import { executeAfter, executeWithMinimumDelay, isDev } from "@agtech/core/utilities/AgTechUtilities"
import AgTechFormDialogDevelopmentPane from "./Development/AgTechFormDialogDevelopmentPane"
import AgTechQueryLoadingSurface from "@agtech/core/data/queries/AgTechQueryLoadingSurface"
import { ErrorBoundary } from "react-error-boundary"
import { useDialogContext } from "@agtech/core/forms/dialogs/AgTechDialogs"

const MultiStepDialogGoToStepEvent = 'agtech:multistep-dialog-go-to-step'

// Moves the open multi-step dialog to the given step without running step validation. Meant for
// guided tours, which need to show a later step's content; submitting still validates as usual.
export const goToMultiStepDialogStep = (stepIndex: number) => {
    window.dispatchEvent(new CustomEvent<number>(MultiStepDialogGoToStepEvent, { detail: stepIndex }));
}

export type AgTechFormDialogStep<TFormData, TContextData> = {
    name: string,
    onValidateStep?: (currentFormEntity: TFormData, context: TContextData) => Promise<boolean>,
    body: (formContext: AgTechFormContext<TFormData, TContextData>) => React.ReactNode
}

export type AgTechMultiStepFormDialogProps<TQueryData, TFormData, TActionData, TResponseData, TContextData = TFormData> = {
    dialogData: AgTechFormDialogDataProps<TQueryData, TFormData, TContextData>,
    dialogAction: AgTechFormActionProps<TFormData, TActionData, TResponseData, TContextData>,
    structure: (data: AgTechFormDialogData<TFormData, TContextData>) => AgTechMultiStepFormDialogStructureProps<TFormData, TContextData>,
    behavior?: AgTechFormDialogBehaviorProps<TActionData, TResponseData>,
    width?: number
}

export type AgTechMultiStepFormDialogStructureProps<TFormData, TContextData> = {
    title: string,
    details?: string,
    headerContent?: () => React.ReactNode,
    steps: AgTechFormDialogStep<TFormData, TContextData>[]
    submitButtonText?: string,
    cancelButtonText?: string,
}

const AgTechMultiStepFormDialogComponent = <TQueryData, TFormData, TActionData, TResponseData, TContextData = TFormData>(
    props: AgTechMultiStepFormDialogProps<TQueryData, TFormData, TActionData, TResponseData, TContextData>
) => {
    const [dialogWidth, setDialogWidth] = useState(props.width ?? 400);

    const formDialog = useAgTechFormDialog({
        formAction: props.dialogAction,
        behavior: props.behavior
    });

    const growDialog = (width: number) => {
        // Dialog width changes take 1 second to transition, so wait at least 1 second
        // when growing the dialog to prevent content from showing while the dialog is growing
        return executeWithMinimumDelay({
            action: async () => {
                setDialogWidth(width);
            },
            minimumDelay: 1000
        });
    }

    return (
        <div
            className="agtech-dialog agtech-multi-step-dialog"
            style={{ width: dialogWidth }}
            onClick={e => e.stopPropagation()}
        >
            <AgTechQueryLoadingSurface<TQueryData, AgTechFormDialogData<TFormData, any>>
                query={props.dialogData.query}
                errorBoundary={AgTechMultiStepFormDialogErrorBoundary}
                projection={queryData => {
                    const dialogData = props.dialogData.getDialogData(queryData);
                    const dialogContext = (props.dialogData.getDialogContext ?? props.dialogData.getDialogData)(queryData);

                    return {
                        formData: dialogData,
                        context: dialogContext
                    }
                }}
            >
                {(data) => {
                    return (
                        <AgTechDataActionSurface
                            action={props.dialogAction.action}
                            behavior={{
                                eventHandlers: {
                                    ...props.dialogAction.eventHandlers,
                                    ...formDialog.dialogDataActionEventHandlers
                                },
                                confirmation: {
                                    renderActionConfirmationView: async (data) => {
                                        return await props.behavior?.confirmation?.renderFormDialogConfirmationView?.(data, {
                                            close: formDialog.closeFormDialog,
                                            grow: growDialog
                                        });
                                    }
                                }
                            }}
                        >
                            {() => (
                                <AgTechForm
                                    initialFormData={data.formData}
                                    contextualData={data.context}
                                    action={props.dialogAction}
                                    structure={{
                                        submitButton: formDialog.dialogSubmissionButton
                                    }}
                                >
                                    {(context) => {
                                        return (
                                            <AgTechMultiStepFormDialogSurface
                                                {...props}
                                                grow={width => setDialogWidth(width)}
                                                formDialog={formDialog}
                                                formContext={context}
                                                structure={props.structure(data)}
                                                behavior={props.behavior ? props.behavior : undefined}
                                            />
                                        )
                                    }}
                                </AgTechForm>
                            )}
                        </AgTechDataActionSurface>
                    )
                }}
            </AgTechQueryLoadingSurface>
        </div>
    )
}

declare type AgTechMultiStepFormDialogSurfaceProps<TFormData, TActionData, TResponseData, TContextData = TFormData> = {
    formContext: AgTechFormContext<TFormData, TContextData>,
    formDialog: AgTechFormDialogType<TActionData, TResponseData>,
    dialogAction: AgTechFormActionProps<TFormData, TActionData, TResponseData, TContextData>,
    structure: AgTechMultiStepFormDialogStructureProps<TFormData, TContextData>,
    behavior?: AgTechFormDialogBehaviorProps<TActionData, TResponseData>,
    grow: (updatedWidth: number) => void
}

const AgTechMultiStepFormDialogSurface = <TFormData, TActionData, TResponseData, TContextData = TFormData>(
    props: AgTechMultiStepFormDialogSurfaceProps<TFormData, TActionData, TResponseData, TContextData>
) => {
    const formDialog = props.formDialog;
    const dialogContext = useDialogContext();
    const [currentStepIndex, setCurrentStepIndex] = useState(0);

    useEffect(() => {
        const onGoToStep = (e: Event) => {
            const requestedIndex = (e as CustomEvent<number>).detail;

            if (requestedIndex >= 0 && requestedIndex < props.structure.steps.length) {
                setCurrentStepIndex(requestedIndex);
            }
        };

        window.addEventListener(MultiStepDialogGoToStepEvent, onGoToStep);
        return () => window.removeEventListener(MultiStepDialogGoToStepEvent, onGoToStep);
    }, [props.structure.steps.length]);

    const currentStep = props.structure.steps[currentStepIndex];
    const isCurrentStepLastStep = currentStepIndex === props.structure.steps.length - 1;

    const stepActionExecutor = useDataActionExecutor();

    const moveToPriorStep = async () => {
        if (formDialog.dialogSubmissionButton.current) {
            formDialog.dialogSubmissionButton.current.click = async () => {};
        }
        
        setCurrentStepIndex(currentStepIndex - 1);
    }

    const moveToNextStep = async () => {
        let isCurrentStepValid = true;

        if (props.formContext) {
            const currentStepSubmissionValidationResult = await stepActionExecutor.validateAction(props.dialogAction.action, {
                originalEntity: props.dialogAction.getActionData(props.formContext.entity, props.formContext.context),
                submittedEntity: props.dialogAction.getActionData(props.formContext.entity, props.formContext.context)
            });

            isCurrentStepValid = currentStepSubmissionValidationResult.wasSuccessful;
        }

        if (currentStep.onValidateStep && props.formContext) {
            isCurrentStepValid = await currentStep.onValidateStep(
                props.formContext.entity,
                props.formContext.context
            );
        }

        if (isCurrentStepValid ?? true) {
            setCurrentStepIndex(curr => curr + 1);
        }
    }

    return (
        <>
            <div className="border-b px-5 py-3 row-based justify-center bg-white flex">
                <AgTechHeaderDetail
                    header={props.structure.title}
                    detail={props.structure.details}
                    settings={{
                        header: {
                            classes: '!text-md'
                        },
                        content: {
                            body: () => (
                                <div className='ml-auto tall column-based flex'>
                                    {props.structure.headerContent?.()}
                                    {props.structure.headerContent ? (
                                        <div className='tall centered pl-4 ml-4 border-l flex'>
                                            <AgTechCloseIcon
                                                onClicked={async (e) => dialogContext.closeDialog(e)}
                                                classes="z-10"
                                                size={32}
                                            />
                                        </div>
                                    ) : (
                                        <AgTechCloseIcon
                                            onClicked={async (e) => dialogContext.closeDialog(e)}
                                            classes="z-10"
                                            size={32}
                                        />
                                    )}
                                </div>
                            )
                        }
                    }}
                />
            </div>
            <div className="column-based stretched overflow-hidden flex">
                <div id='multistep-dialog-step-panel' className='row-based tall px-5 py-7 border-r w-[240px] flex'>
                    <div className='row-based tall ml-2 flex'>
                        {props.structure.steps.map((step, index) => {
                            const stepDotDiameter = 14;

                            const isActiveStep = currentStepIndex === index;
                            const isCompletedStep = index < currentStepIndex;

                            const borderClasses = index === props.structure.steps.length - 1 ? '' : 'border-l';
                            const activeClasses = isActiveStep ? 'color-highlight' : isCompletedStep ? 'color-green' : 'color-gray';

                            return  (
                                <div key={step.name} className={`column-based ${borderClasses} container`} style={{ height: 56 }}>
                                    <div
                                        className={`${activeClasses} rounded-circle border mr-3`}
                                        style={{
                                            width: stepDotDiameter,
                                            height: stepDotDiameter,
                                            marginLeft: 0 - (stepDotDiameter / 2)
                                        }}
                                    />
                                    <h1 className='text-sm font-semibold heading-[14px]' style={{ lineHeight: stepDotDiameter + 'px' }}>{step.name}</h1>
                                </div>
                            );
                        })}
                    </div>
                </div>
                <div id='multistep-dialog-content' className="flexed wide relative flex">
                    <div className="row-based stretched pt-2 pb-4 bg-gray-100 flex">
                        <AgTechMultiStepFormDialogBody
                            {...props}
                            currentStep={currentStep}
                            dialogSubmissionButton={formDialog.dialogSubmissionButton}
                        />
                    </div>
                </div>
            </div>
            <div className="agtech-dialog-footer pt-5 top-bordered">
                <AgTechButton
                    key={'Previous'}
                    text={'Back'}
                    icon="fas fa-chevron-left"
                    action={moveToPriorStep}
                    classes="pr-6 justify-center w-[100px] mr-4"
                    iconClasses="mr-auto"
                    disableIf={currentStepIndex === 0}
                />
                { isCurrentStepLastStep ? (
                    <AgTechButton
                        key={'Submit'}
                        text={(props.structure.submitButtonText ?? 'Submit')}
                        icon={'fas fa-check'}
                        iconOnLeft={true}
                        classes='button-highlight min-w-[100px] justify-center'
                        action={() => formDialog.dialogSubmissionButton.current?.click()}
                    />
                ) : (
                    <AgTechButton
                        key={'Next'}
                        text={'Next'}
                        icon={'fas fa-chevron-right'}
                        iconOnLeft={false}
                        iconClasses="ml-6"
                        action={moveToNextStep}
                        classes="pl-6 button-highlight justify-center w-[100px]"
                    />
                )}
                
            </div>
        </>
    )
}

declare type AgTechMultiStepFormDialogBodyProps<TFormData, TActionData, TResponseData, TContextData = TFormData> =
    AgTechMultiStepFormDialogSurfaceProps<TFormData, TResponseData, TActionData, TContextData> & {
    currentStep: AgTechFormDialogStep<TFormData, TContextData>,
    dialogSubmissionButton: React.RefObject<AgTechFormSubmissionButton>
}

const AgTechMultiStepFormDialogBody = <TFormData, TActionData, TResponseData, TContextData = TFormData>(
    props: AgTechMultiStepFormDialogBodyProps<TFormData, TActionData, TResponseData, TContextData>
) => {
    return (
        <div className="flexed scrollable row-based px-5 flex">
            <AgTechFadeInView key={props.currentStep.name} classes="row-based vertical-pane flex">
                {props.currentStep.body(props.formContext)}
                {isDev() && <AgTechFormDialogDevelopmentPane formContext={props.formContext} formActionData={props.dialogAction.getActionData} />}
            </AgTechFadeInView>
        </div>
    )
}

const AgTechMultiStepFormDialogErrorBoundary = (props: {
    children: React.ReactNode
}) => {
    const dialogContext = useDialogContext();

    return (
        <ErrorBoundary
            fallbackRender={(props) => {
                const errorDetails = {
                    message: props.error.message,
                    callStack: props.error.stack,
                };

                return (
                    <div className='stretched centered relative row-based flex'>
                        <AgTechCloseIcon
                            onClicked={async (e) => dialogContext.closeDialog(e)} 
                            classes="absolute top-[20px] right-[16px] z-10"
                            size={32}
                        />
                        <i className={`fas fa-exclamation text-md w-[32px] h-[32px] bg-red font-color-white mb-4 centered rounded-[50%] flex`} />
                        <h1 className='text-xl font-semibold mb-2' >An error occurred.</h1>
                        <h3 className='text-plus text-detail'>Please close and open this dialog again.</h3>
                        {isDev() && (
                            <textarea className="mt-4 w-[60%] min-h-[240px]">
                                {JSON.stringify(errorDetails, null, 4)}
                            </textarea>
                        )}
                    </div>
                )
            }}
        >
            {props.children}
        </ErrorBoundary>
    )
}

const AgTechMultiStepFormDialog = React.memo(AgTechMultiStepFormDialogComponent, (prev, curr) => {
    return prev.dialogData.query.queryResult.data === curr.dialogData.query.queryResult.data;
}) as typeof AgTechMultiStepFormDialogComponent;

export default AgTechMultiStepFormDialog;